import {
  Layers, Target, Zap, Users, Database, Image as ImageIcon, FileText, Wallet, LineChart, Tag, Link2,
  MousePointerClick, Gauge, ClipboardList, Search, Filter, ShoppingCart, Sparkles, Repeat, Globe, Radio,
} from "lucide-react";

const tiers = (platform, audit, premium) => [
  {
    name: `${platform} Health Check`,
    tag: "Free",
    usd: 0,
    features: ["5-7 key findings", "Health score snapshot", "Top 3 priority issues", "Delivered via email"],
    cta: "Get Free Check",
  },
  {
    name: `${platform} Account Audit`,
    tag: "Most requested",
    usd: audit,
    features: [
      "20-30 page audit report",
      "15+ performance checks",
      "Prioritized recommendations",
      "Custom action plan",
      "Delivered in 3-5 business days",
    ],
    cta: "Get Your Audit",
    featured: true,
  },
  {
    name: `${platform} Audit + Strategy Session`,
    tag: "Premium",
    usd: premium,
    features: [
      "Everything in the Account Audit",
      "60-minute strategy call",
      "Custom growth roadmap",
      "Live Q&A with Abhinav Dhiman",
    ],
    cta: "Book Now",
  },
];

export const AUDITS = {
  linkedin: {
    key: "linkedin",
    bestFor: "B2B teams targeting by job title, company and seniority — ABM and lead gen.",
    path: "/linkedin-ads-audit",
    platform: "LinkedIn Ads",
    short: "LinkedIn",
    accent: "#0A66C2",
    intro:
      "A deep-dive audit of your LinkedIn Ads account to uncover hidden issues, missed opportunities, and a clear roadmap to improve performance and ROI.",
    seoDescription:
      "A deep-dive LinkedIn Ads account audit — structure, targeting, ABM, creative, Lead Gen Forms, tracking and spend — with a prioritized action plan.",
    included: [
      [Layers, "Account Structure & Setup"],
      [Target, "Campaign Architecture Review"],
      [Zap, "Objective & Funnel Alignment"],
      [Users, "Audience & Targeting Analysis"],
      [Database, "ABM / Company Targeting"],
      [ImageIcon, "Ad Creative & Format Analysis"],
      [FileText, "Lead Gen Form Optimization"],
      [Wallet, "Budget & Bidding Evaluation"],
      [LineChart, "Performance Deep Dive (CTR, CPL, CPA)"],
      [Tag, "Insight Tag & Conversion Tracking"],
      [Link2, "UTM & Attribution Check"],
      [MousePointerClick, "Landing Page Evaluation"],
      [Gauge, "Frequency & Audience Saturation"],
      [ClipboardList, "Prioritized Action Plan & Roadmap"],
    ],
    sample: { score: 68, stats: [["Spend Analyzed", "$84,231"], ["Avg. CPL", "$37.42"], ["Issues Found", "15"]] },
    issues: [
      "Campaign structure needs optimization",
      "Audience overlap hurting performance",
      "Low CTR on key ad sets",
      "Tracking gaps in conversion setup",
    ],
    outcome: "Lower CPL, better lead quality, and a LinkedIn engine built for pipeline.",
    credentials: ["14+ years in B2B marketing", "Managed $1M+ in LinkedIn ad spend", "Data-driven, results-focused"],
    tiers: tiers("LinkedIn Ads", 149, 349),
  },
  google: {
    key: "google",
    bestFor: "Teams capturing high-intent search demand — Search, Performance Max and Shopping.",
    path: "/google-ads-audit",
    platform: "Google Ads",
    short: "Google",
    accent: "#4285F4",
    intro:
      "A deep-dive audit of your Google Ads account — search terms, match types, Quality Score, Performance Max and tracking — with a clear roadmap to cut waste and improve ROI.",
    seoDescription:
      "A deep-dive Google Ads account audit — search terms and negatives, match types, Quality Score, Performance Max, Shopping and conversion tracking.",
    included: [
      [Layers, "Account Structure & Naming"],
      [Target, "Campaign Types & Goals Review"],
      [Search, "Search Terms & Negative Keywords"],
      [Filter, "Keyword & Match Type Strategy"],
      [Gauge, "Quality Score & Ad Relevance"],
      [ImageIcon, "RSAs, Assets & Ad Copy"],
      [Sparkles, "Performance Max Asset Groups & Signals"],
      [ShoppingCart, "Shopping & Merchant Feed Health"],
      [Wallet, "Smart Bidding & Budget Evaluation"],
      [Tag, "Google Tag & Enhanced Conversions"],
      [Link2, "GA4, Attribution & Offline Imports"],
      [Users, "Audiences & Customer Match"],
      [MousePointerClick, "Landing Page Experience"],
      [ClipboardList, "Prioritized Action Plan & Roadmap"],
    ],
    sample: { score: 64, stats: [["Spend Analyzed", "$62,480"], ["Avg. CPA", "$118.20"], ["Issues Found", "17"]] },
    issues: [
      "Irrelevant search terms with no negatives",
      "Broad match running without Smart Bidding signals",
      "Performance Max cannibalising brand search",
      "Duplicate conversion actions inflating results",
    ],
    outcome: "Less wasted spend, cleaner intent capture, and Google Ads built for pipeline.",
    credentials: ["14+ years in B2B marketing", "Search, PMax & Shopping expertise", "Data-driven, results-focused"],
    tiers: tiers("Google Ads", 149, 349),
  },
  meta: {
    key: "meta",
    bestFor: "Teams using Facebook and Instagram for retargeting, lookalikes and lower-cost reach.",
    path: "/meta-ads-audit",
    platform: "Meta Ads",
    short: "Meta",
    accent: "#0064E0",
    intro:
      "A deep-dive audit of your Meta Ads account — Advantage+ setup, the Pixel and Conversions API, creative fatigue and audience overlap — with a clear roadmap to improve ROI.",
    seoDescription:
      "A deep-dive Meta Ads account audit — Advantage+ setup, Pixel and Conversions API, creative fatigue, audience overlap, Instant Forms and attribution.",
    included: [
      [Layers, "Business Portfolio & Account Setup"],
      [Target, "Campaign Objectives & Structure"],
      [Sparkles, "Advantage+ Campaign & Audience Setup"],
      [Users, "Audience Targeting & Overlap"],
      [Database, "Custom & Lookalike Audiences"],
      [ImageIcon, "Creative Mix & Placements"],
      [Repeat, "Creative Fatigue & Frequency"],
      [FileText, "Instant Forms Optimization"],
      [Wallet, "Budget, Bidding & Learning Phase"],
      [Tag, "Meta Pixel & Conversions API"],
      [Gauge, "Event Match Quality"],
      [Link2, "Attribution Settings & UTMs"],
      [MousePointerClick, "Landing Page Evaluation"],
      [ClipboardList, "Prioritized Action Plan & Roadmap"],
    ],
    sample: { score: 61, stats: [["Spend Analyzed", "$38,960"], ["Avg. CPL", "$24.10"], ["Issues Found", "16"]] },
    issues: [
      "Creative fatigue on top-spending ads",
      "Audience overlap across ad sets",
      "Conversions API not deduplicated with Pixel",
      "Ad sets stuck in the learning phase",
    ],
    outcome: "Fresher creative, cleaner signals, and Meta Ads built for pipeline.",
    credentials: ["14+ years in B2B marketing", "Pixel, CAPI & Advantage+ expertise", "Data-driven, results-focused"],
    tiers: tiers("Meta Ads", 99, 249),
  },
  bing: {
    key: "bing",
    bestFor: "Teams extending search to Microsoft’s professional audience at lower CPCs.",
    path: "/bing-ads-audit",
    platform: "Bing Ads",
    short: "Bing",
    accent: "#00897B",
    intro:
      "A deep-dive audit of your Microsoft Advertising account — Google import health, LinkedIn profile targeting, the Audience Network and UET tracking — with a clear roadmap to improve ROI.",
    seoDescription:
      "A deep-dive Bing Ads (Microsoft Advertising) account audit — Google import health, LinkedIn profile targeting, Audience Network, UET tracking and bidding.",
    included: [
      [Layers, "Account Structure & Setup"],
      [Repeat, "Google Import Health & Mapping"],
      [Target, "Campaign Types & Goals Review"],
      [Search, "Search Terms & Negative Keywords"],
      [Filter, "Keyword & Match Type Strategy"],
      [Users, "LinkedIn Profile Targeting"],
      [Globe, "Audience Network Placements"],
      [Radio, "Search Partner Traffic Quality"],
      [ImageIcon, "Ads, Assets & Extensions"],
      [Wallet, "Bidding & Budget Evaluation"],
      [Tag, "UET Tag & Conversion Goals"],
      [Link2, "Attribution, msclkid & UTMs"],
      [MousePointerClick, "Landing Page Evaluation"],
      [ClipboardList, "Prioritized Action Plan & Roadmap"],
    ],
    sample: { score: 66, stats: [["Spend Analyzed", "$21,740"], ["Avg. CPA", "$86.50"], ["Issues Found", "14"]] },
    issues: [
      "Google import overwriting local changes",
      "LinkedIn profile bid-ups not applied",
      "Audience Network placements wasting spend",
      "UET conversion goals misconfigured",
    ],
    outcome: "Lower CPCs, senior B2B reach, and Microsoft Advertising built for pipeline.",
    credentials: ["14+ years in B2B marketing", "Microsoft Advertising & UET expertise", "Data-driven, results-focused"],
    tiers: tiers("Bing Ads", 99, 249),
  },
};

export const AUDIT_LIST = Object.values(AUDITS);
